import React from "react";
import { showModal } from "../../../../../../common-deprecated/redux/actions/ModalActions";
import type { UscDispatchType } from "../../../../redux/store";
import { useUscSelector } from "../../../../redux/store";
import { UscLabel } from "../../../../utils/labels";
import {
    CarDeliveryDestination,
    CarDeliveryTransportationOption,
    createFullAddress,
} from "../../../../utils/carDeliveryUtils";
import { setCarDeliveryConfig } from "../../../../redux/actions/UscCarDeliveryActions";
import { calculateExternalPrice } from "../../../../redux/actions/UscExternalPriceActions";
import { formatPriceIntl } from "../../../../../../common-deprecated/priceUtils";
import {
    trackBasketRemoveItem,
    trackCarDeliveryDiscoverMoreClick,
    trackCta,
    UscTrackingLocation,
} from "../../../../utils/tracking";
import Card from "../../styles/molecules/Card";
import Text from "../../styles/atoms/Text";
import Button from "../../styles/atoms/Button";
import Icon from "../../styles/Icon";
import Checkbox from "../../styles/atoms/Checkbox";
import * as Styles from "./CarDeliveryService.styles";
import * as CardStyles from "../../styles/molecules/Card.styles";
import { MODAL_USC_CAR_DELIVERY, MODAL_USC_CAR_DELIVERY_INFO } from "../../../../utils/modalConstants";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

export const CarDelivery = (): JSX.Element | null => {
    const dispatch = useDispatch<UscDispatchType>();
    const carDeliveryConfiguration = useUscSelector((state) => state.carDelivery);
    const deliveryConfig = useUscSelector((state) => state.carDelivery.deliveryConfig);
    const commonSettings = useUscSelector((state) => state.commonSettings);
    const uscEnv = useUscSelector((state) => state.vehicle.type);

    // Car Delivery is only available when the financing option is cash.
    const deliveryEnabled = useUscSelector((state) => state.basket.activeFinanceOption === "cash");

    const openCarDeliveryModal = (): void => {
        if (deliveryEnabled) {
            trackCta("delivery-calculator", UscTrackingLocation.Extras, uscEnv);
            dispatch(showModal({ type: MODAL_USC_CAR_DELIVERY }));
        }
    };

    const removeCarDeliveryConfig = (): void => {
        trackBasketRemoveItem(UscTrackingLocation.Extras, uscEnv, "carDelivery");
        dispatch(setCarDeliveryConfig(null));
        dispatch(calculateExternalPrice());
    };

    return (
        <Card role="option" variant="delivery" aria-selected={!!deliveryConfig}>
            <CardStyles.Header>
                <div>
                    <Icon variant="courtesy-van" iconSize="medium" />
                    <Text tag="h3" variant="h6">
                        <UscLabel label="uscCarDeliveryServiceTitle" />
                    </Text>
                </div>
                <div>
                    <Text tag="span" variant="body-small-regular" color="grey-7">
                        <UscLabel label="uscServicesOptional" />
                    </Text>
                </div>
            </CardStyles.Header>
            <CardStyles.Body>
                <Text tag="div" variant="body-book" spacing={{ mb: "spacing-1" }}>
                    <UscLabel label="uscCarDeliveryServiceDescription" />
                </Text>
                <Button
                    variant="link"
                    size="small"
                    onClick={() => {
                        trackCarDeliveryDiscoverMoreClick(uscEnv);
                        dispatch(showModal({ type: MODAL_USC_CAR_DELIVERY_INFO }));
                    }}
                >
                    <UscLabel label="uscCarDeliveryServiceButton" />
                </Button>
            </CardStyles.Body>
            <Styles.Footer active={!!carDeliveryConfiguration.initialized && !!carDeliveryConfiguration.deliveryConfig}>
                <Styles.DeliveryInfo>
                    {carDeliveryConfiguration.initialized && carDeliveryConfiguration.deliveryConfig && (
                        <Styles.DeliveryConfig>
                            <Styles.DeliveryConfigHeader>
                                {carDeliveryConfiguration.deliveryConfig.destination ===
                                CarDeliveryDestination.Dealer ? (
                                    <UscLabel label="uscCarDeliveryServiceDestinationDealer" />
                                ) : (
                                    <UscLabel label="uscCarDeliveryServiceDestinationHome" />
                                )}
                            </Styles.DeliveryConfigHeader>
                            <Styles.DeliveryConfigContent>
                                <div>
                                    <Text tag="div" variant="body-book">
                                        {createFullAddress(carDeliveryConfiguration.deliveryConfig.address)}
                                    </Text>
                                    <Styles.DeliveryMethod>
                                        {carDeliveryConfiguration.deliveryConfig.transportationType ===
                                        CarDeliveryTransportationOption.ProfessionalDriver ? (
                                            <>
                                                <Icon variant="steering-wheel" />
                                                <Text tag="div" variant="body-regular">
                                                    <UscLabel label="uscCarDeliverySelectTransportationOptionProfessionalTitle" />
                                                </Text>
                                            </>
                                        ) : (
                                            <>
                                                <Icon variant="truck" />
                                                <Text tag="div" variant="body-regular">
                                                    <UscLabel label="uscCarDeliverySelectTransportationOptionTruckTitle" />
                                                </Text>
                                            </>
                                        )}
                                    </Styles.DeliveryMethod>
                                </div>
                                <div>
                                    <Button variant="link" size="small" onClick={openCarDeliveryModal}>
                                        <UscLabel label="uscCarDeliveryServiceEditButton" />
                                    </Button>
                                </div>
                            </Styles.DeliveryConfigContent>
                        </Styles.DeliveryConfig>
                    )}
                </Styles.DeliveryInfo>
                <Styles.CtaTrigger
                    onClick={() => {
                        if (!carDeliveryConfiguration.deliveryConfig) openCarDeliveryModal();
                        else if (carDeliveryConfiguration.deliveryConfig) removeCarDeliveryConfig();
                    }}
                    disabled={!deliveryEnabled}
                >
                    {carDeliveryConfiguration.initialized && carDeliveryConfiguration.deliveryConfig && (
                        <Text tag="div" variant="body-semibold">
                            {deliveryConfig?.price &&
                                formatPriceIntl(commonSettings, deliveryConfig.price?.cashPriceInclVat || 0)
                                    .primaryPrice}
                        </Text>
                    )}
                    <Styles.Cta>
                        {!deliveryEnabled && (
                            <Text variant="body-book" color="grey-7">
                                <UscLabel label="uscCarDeliveryServiceUnavailable" />
                            </Text>
                        )}
                        {deliveryEnabled && !carDeliveryConfiguration.deliveryConfig && (
                            <Text variant="body-book">
                                <UscLabel label="uscCarDeliveryServiceAddDeliveryButton" />
                            </Text>
                        )}
                        {!carDeliveryConfiguration.deliveryConfig && (
                            <Checkbox
                                variant="pack"
                                id="checkbox-delivery-service-unavailable"
                                title="uscCarDeliveryServiceUnavailable"
                                checked={!!carDeliveryConfiguration.deliveryConfig}
                                disabled={!deliveryEnabled}
                                onClick={openCarDeliveryModal}
                            >
                                <Icon variant="plus" />
                            </Checkbox>
                        )}
                        {carDeliveryConfiguration.deliveryConfig && (
                            <>
                                <Checkbox
                                    variant="pack"
                                    id="checkbox-delivery-config"
                                    title="car delivery service unavailable"
                                    checked={!!carDeliveryConfiguration.deliveryConfig}
                                    onClick={removeCarDeliveryConfig}
                                >
                                    <Icon variant={deliveryConfig ? "close" : "plus"} />
                                </Checkbox>
                            </>
                        )}
                    </Styles.Cta>
                </Styles.CtaTrigger>
            </Styles.Footer>
        </Card>
    );
};

export default CarDelivery;
